import React from 'react';
import {
  AlignHorizontalJustifyCenter,
  AlignHorizontalJustifyEnd,
  AlignHorizontalJustifyStart,
  AlignHorizontalSpaceBetween,
  AlignVerticalJustifyCenter,
  AlignVerticalJustifyEnd,
  AlignVerticalJustifyStart,
  AlignVerticalSpaceBetween,
  WandSparkles
} from 'lucide-react';
import { useUiStateStore } from '@/stores/uiStateStore';
import { useGroupStore } from '@/stores/groupStore';
import { useScene } from '@/hooks/useScene';
import type { ItemReference } from '@/types';
import {
  alignElements,
  AlignAction,
  distributeElements,
  DistributionAction,
  getLayoutElement,
  LayoutElement,
  LayoutMove,
  tidyElements
} from '@/core/layout/layoutUtils';

interface LayoutSubject {
  element: LayoutElement;
  members: ItemReference[];
}

const keyOf = (ref: ItemReference) => `${ref.type}:${ref.id}`;

export const LayoutActions: React.FC = () => {
  const selection = useUiStateStore((state) => state.itemControls);
  const groups = useGroupStore((state) => state.groups);
  const scene = useScene();

  const subjects = React.useMemo<LayoutSubject[]>(() => {
    if (selection?.type !== 'MULTI_SELECT') return [];
    const result: LayoutSubject[] = [];
    const visitedGroups = new Set<string>();
    const visitedMembers = new Set<string>();

    selection.items.forEach((selected) => {
      const group = groups.find((candidate) => candidate.elements.some((member) =>
        member.type === selected.type && member.id === selected.id
      ));
      const members: ItemReference[] = group
        ? group.elements.map((member) => ({ ...member }))
        : [selected];
      if (group) {
        if (visitedGroups.has(group.id)) return;
        visitedGroups.add(group.id);
      }

      const movableMembers = members.filter((member) =>
        member.type === 'ITEM' || member.type === 'RECTANGLE' || member.type === 'TEXTBOX'
      ).filter((member) => {
        const key = keyOf(member);
        if (visitedMembers.has(key)) return false;
        visitedMembers.add(key);
        return true;
      });
      const bounds = movableMembers
        .map((member) => getLayoutElement(member, scene))
        .filter((element): element is LayoutElement => Boolean(element));
      if (!bounds.length) return;
      result.push({
        element: {
          ref: bounds[0].ref,
          from: {
            x: Math.min(...bounds.map((bound) => bound.from.x)),
            y: Math.min(...bounds.map((bound) => bound.from.y))
          },
          to: {
            x: Math.max(...bounds.map((bound) => bound.to.x)),
            y: Math.max(...bounds.map((bound) => bound.to.y))
          }
        },
        members: movableMembers
      });
    });
    return result;
  }, [selection, scene, groups]);

  if (subjects.length < 2) return null;

  const applyMoves = (moves: LayoutMove[]) => {
    const subjectByRef = new Map(subjects.map((subject) => [keyOf(subject.element.ref), subject]));
    moves.forEach((move) => {
      if (move.delta.x === 0 && move.delta.y === 0) return;
      const subject = subjectByRef.get(keyOf(move.ref));
      subject?.members.forEach((member) => {
        if (member.type === 'ITEM') {
          const item = scene.items.find((candidate) => candidate.id === member.id);
          if (item) scene.updateViewItem(member.id, {
            tile: { x: item.tile.x + move.delta.x, y: item.tile.y + move.delta.y }
          });
        } else if (member.type === 'RECTANGLE') {
          const rectangle = scene.rectangles.find((candidate) => candidate.id === member.id);
          if (rectangle) scene.updateRectangle(member.id, {
            from: {
              x: rectangle.from.x + move.delta.x,
              y: rectangle.from.y + move.delta.y
            },
            to: {
              x: rectangle.to.x + move.delta.x,
              y: rectangle.to.y + move.delta.y
            }
          });
        } else if (member.type === 'TEXTBOX') {
          const textBox = scene.textBoxes.find((candidate) => candidate.id === member.id);
          if (textBox) scene.updateTextBox(member.id, {
            tile: {
              x: textBox.tile.x + move.delta.x,
              y: textBox.tile.y + move.delta.y
            }
          });
        }
      });
    });
  };

  const align = (action: AlignAction) => applyMoves(alignElements(
    subjects.map((subject) => subject.element), action
  ));
  const distribute = (action: DistributionAction) => applyMoves(distributeElements(
    subjects.map((subject) => subject.element), action
  ));
  const buttonClass = 'rounded-md p-1.5 text-slate-600 transition hover:bg-blue-50 hover:text-blue-700 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-blue-300';

  return (
    <div
      data-editor-ui="true"
      className="fixed left-1/2 top-20 z-[120] flex -translate-x-1/2 items-center gap-0.5 rounded-xl border border-slate-200 bg-white/95 p-1 shadow-lg backdrop-blur dark:border-slate-700 dark:bg-slate-800/95"
      onMouseDown={(event) => event.stopPropagation()}
      aria-label="Layout selected objects"
    >
      <button className={buttonClass} onClick={() => align('LEFT')} title="Align left"><AlignHorizontalJustifyStart size={17} /></button>
      <button className={buttonClass} onClick={() => align('CENTER_X')} title="Align horizontal centers"><AlignHorizontalJustifyCenter size={17} /></button>
      <button className={buttonClass} onClick={() => align('RIGHT')} title="Align right"><AlignHorizontalJustifyEnd size={17} /></button>
      <span className="mx-1 h-5 w-px bg-slate-200 dark:bg-slate-700" />
      <button className={buttonClass} onClick={() => align('TOP')} title="Align top"><AlignVerticalJustifyStart size={17} /></button>
      <button className={buttonClass} onClick={() => align('CENTER_Y')} title="Align vertical centers"><AlignVerticalJustifyCenter size={17} /></button>
      <button className={buttonClass} onClick={() => align('BOTTOM')} title="Align bottom"><AlignVerticalJustifyEnd size={17} /></button>
      <span className="mx-1 h-5 w-px bg-slate-200 dark:bg-slate-700" />
      <button className={buttonClass} onClick={() => distribute('HORIZONTAL')} title="Distribute horizontally" disabled={subjects.length < 3}><AlignHorizontalSpaceBetween size={17} /></button>
      <button className={buttonClass} onClick={() => distribute('VERTICAL')} title="Distribute vertically" disabled={subjects.length < 3}><AlignVerticalSpaceBetween size={17} /></button>
      <span className="mx-1 h-5 w-px bg-slate-200 dark:bg-slate-700" />
      <button className={buttonClass} onClick={() => applyMoves(tidyElements(subjects.map((subject) => subject.element)))} title="Tidy up"><WandSparkles size={17} /></button>
    </div>
  );
};
